import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { FEATURES } from '../config/features';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../assets/LoadingSpinner';
import { ReactComponent as SeeIcon } from '../assets/icons/SeeIcon.svg';
import { ReactComponent as NoSeeIcon } from '../assets/icons/NoSeeIcon.svg';
import { useToast } from '../contexts/ToastContext';

const BACKEND_URL = 'https://backend-288175208699.europe-west1.run.app';

// Un usuario 'Registrado' ya tiene cuenta creada — no tiene sentido pedirle
// de nuevo nombre/email/contraseña. Esta pantalla corta llama directo a
// /unirse-lista-espera con su sesión, sin repetir datos que ya tenemos.
const SumarseALaListaRegistrado = () => {
    const { user } = useAuth();
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState(null); // { estado, mensaje } | { error }
    const [popupResultadoAbierto, setPopupResultadoAbierto] = useState(true);

    const handleSumarse = async () => {
        setEnviando(true);
        setResultado(null);
        try {
            const idToken = await user.getIdToken();
            const response = await fetch(`${BACKEND_URL}/unirse-lista-espera`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
                // POST sin body: Cloud Run devuelve 411 (Length Required) contra
                // algunas conexiones si no hay Content-Length explícito. Un
                // body vacío pero real evita el problema.
                body: JSON.stringify({}),
            });
            const data = await response.json();
            if (response.ok) {
                setResultado({ estado: data.estado_lista });
            } else {
                setResultado({ error: data.error || 'No se pudo completar la inscripción.' });
            }
        } catch (error) {
            console.error('Error al unirse a la lista de espera:', error);
            setResultado({ error: 'Error de conexión con el servidor.' });
        } finally {
            setEnviando(false);
        }
    };

    if (resultado?.estado) {
        return (
            <div className="historia-container">
                <div className="contenido-scrollable">
                    <div className="titulo-principal">
                        <hr className="linea-horizontal" />
                        <h1>¡LISTO!</h1>
                        <hr className="linea-horizontal" />
                    </div>
                    <p className="contenido-centrado">
                        Podés ver el detalle en cualquier momento desde <Link to="/perfil">tu perfil</Link>.
                    </p>
                </div>
                {resultado.estado === 'Activo' ? (
                    <ActivoPopup isOpen={popupResultadoAbierto} onClose={() => setPopupResultadoAbierto(false)} />
                ) : (
                    <EsperaPopup isOpen={popupResultadoAbierto} onClose={() => setPopupResultadoAbierto(false)} />
                )}
            </div>
        );
    }

    return (
        <div className="historia-container">
            <div className="contenido-scrollable">
                <div className="titulo-principal">
                    <hr className="linea-horizontal" />
                    <h1>ACCESO EXCLUSIVO: PROYECTO P195</h1>
                    <hr className="linea-horizontal" />
                </div>
                <p className="contenido-centrado">
                    La producción de P195 es extremadamente limitada para garantizar la máxima calidad en cada botella. Por este motivo, la única forma de adquirir nuestros vinos es inscribiéndose en la lista de espera.
                    <div style={{ height: '10px' }}></div>
                    La inscripción a esta lista no garantiza un cupo automático. Las asignaciones se realizan por estricto orden de registro, a medida que surgen disponibilidades por la baja de clientes activos.
                </p>
                {resultado?.error && (
                    <p className="contenido-centrado" style={{ color: '#8f1941' }}>{resultado.error}</p>
                )}
                <button
                    className="boton-inscribirse"
                    onClick={handleSumarse}
                    disabled={enviando}
                    style={{ padding: ' 15px 50px', margin: '50px auto 0', display: 'block' }}
                >
                    {enviando ? 'Enviando…' : 'Sumarse a la lista de espera'}
                </button>
            </div>
        </div>
    );
};
const SuccessModal = ({ isOpen, onClose, message }) => {
if (!isOpen) return null;
return (
<div className="modal-backdrop">
<div className="modal-content">
<div className="modal-close" onClick={onClose}>&times;</div>
<h1>REGISTRO COMPLETADO CON ÉXITO</h1>
<p>Hemos recibido sus datos correctamente y su nombre ha sido añadido a nuestra lista de espera.
</p>
<p>Nos comunicaremos con usted en cuanto un cupo de asignación esté disponible. A partir de ahora, recibirá también nuestras comunicaciones con acceso prioritario a eventos y degustaciones.
</p>
<button className="modal-button" onClick={onClose}>Ingreso a mi perfil</button>
</div>
</div>
);
};
// Se muestra cuando la inscripción a la lista de espera resulta en cupo
// asignado de una — a diferencia de SuccessModal (que asume que quedaste
// en espera), acá el mensaje y la acción son los correctos para alguien
// que ya puede comprar.
const irATienda = () => { window.location.href = '/cupo-asignado'; };
const irAShop = () => { window.location.href = '/shop'; };
const ActivoPopup = ({ isOpen }) => {
// Navegación con recarga completa a propósito (no react-router): el
// AuthContext trae el perfil (estadoLista) una sola vez al cargar la app,
// así que un navigate() de cliente dejaría el 'Registrado' viejo en memoria
// y Cupo.js te rebotaría de vuelta. Recargando, AuthContext vuelve a pedir
// el perfil fresco (ya 'Activo') antes de que se renderice /cupo-asignado. La X
// lleva al mismo destino que el botón, no se queda en esta pantalla.
if (!isOpen) return null;
return (
<div className="modal-backdrop">
<div className="modal-content">
<div className="modal-close" onClick={irATienda}>&times;</div>
<h1>¡BIENVENIDO AL CÍRCULO!</h1>
<p style={{margin: '0 auto 5px'}}>Había cupo disponible en el momento de tu inscripción, así que ya tenés acceso a nuestra producción limitada de P195.</p>
<p>Para activar formalmente tu cupo, recordá realizar tu primera compra dentro de los próximos 30 días.</p>
<button className="modal-button" onClick={irATienda}>Ir a mi cupo</button>
</div>
</div>
);
};
// Contracara de ActivoPopup: cuando la inscripción te deja en 'Espera' (no
// había cupo libre en el momento). Mismo protagonismo que el de Activo, para
// que quede claro que la inscripción sí se hizo, solo que no hay cupo todavía.
// Misma razón que ActivoPopup para usar recarga completa en vez de navigate().
const EsperaPopup = ({ isOpen }) => {
if (!isOpen) return null;
return (
<div className="modal-backdrop">
<div className="modal-content">
<div className="modal-close" onClick={irAShop}>&times;</div>
<h1>¡TE SUMASTE A LA LISTA!</h1>
<p style={{margin: '0 auto 5px'}}>Por el momento no hay cupo disponible, pero ya quedaste anotado en la lista de espera del proyecto P195.</p>
<p>Te enviaremos un correo en cuanto se libere un cupo. Mientras tanto, podés seguir explorando nuestro Shop.</p>
<button className="modal-button" onClick={irAShop}>Ir a Shop</button>
</div>
</div>
);
};
const MensajeError = ({ mensaje }) => (
<span className="mensaje-obligatorio">{mensaje}</span>
);
const isAdult = (dateOfBirth) => {
    if (!dateOfBirth) return true;
    const parts = dateOfBirth.split('/');
    if (parts.length !== 3) return false; 
    const formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`; 
    const today = new Date();
    const birthDate = new Date(formattedDate);
    if (isNaN(birthDate)) return false; 
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age >= 18;
};
const formatDateInput = (value) => {
    let digits = value.replace(/\D/g, '');
    let formatted = '';
    if (digits.length > 0) {
        formatted += digits.substring(0, 2);
    }
    if (digits.length >= 3) {
        formatted += '/' + digits.substring(2, 4);
    }
    if (digits.length >= 5) {
        formatted += '/' + digits.substring(4, 8);
    }
    return formatted;
};
const formatDateForSubmission = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
};
function ListaDeEspera() {
    const { user, loading: authLoading } = useAuth();
    const { showToast } = useToast();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isActivoModalOpen, setIsActivoModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const initialFormData = {
        nombre: '', apellido: '', contrasena: '', repetirContrasena: '',
        email: '', repetirEmail: '', telefono: '', 
        calle: '', altura: '', ciudad: '', codigoPostal: '', pais: '',
        titulo: '', empresa: '', fechaDeNacimiento: ''
    };
    const [formData, setFormData] = useState(initialFormData);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [showRepeatEmail, setShowRepeatEmail] = useState(false);
    const togglePasswordVisibility = (field) => {
        if (field === 'contrasena') {
            setShowPassword(prev => !prev);
        } else if (field === 'repetirContrasena') {
            setShowRepeatPassword(prev => !prev);
        } else if (field === 'email') { 
            setShowEmail(prev => !prev);
        } else if (field === 'repetirEmail') {
            setShowRepeatEmail(prev => !prev);
        }
    };
    const checkMatchError = (name, value, newErrors) => {
        let errorUpdated = false;
        if (name === 'contrasena' || name === 'repetirContrasena') {
            const currentContrasena = name === 'contrasena' ? value : formData.contrasena;
            const currentRepetirContrasena = name === 'repetirContrasena' ? value : formData.repetirContrasena;
            if (currentContrasena === currentRepetirContrasena && currentContrasena !== '') {
                delete newErrors.contrasena;
                delete newErrors.repetirContrasena;
                errorUpdated = true;
            } else if (currentContrasena !== currentRepetirContrasena && currentRepetirContrasena !== '') {
                newErrors.repetirContrasena = 'Las contraseñas no coinciden';
                errorUpdated = true;
            }
        }
        if (name === 'email' || name === 'repetirEmail') {
            const currentEmail = name === 'email' ? value : formData.email;
            const currentRepetirEmail = name === 'repetirEmail' ? value : formData.repetirEmail;
            if (currentEmail === currentRepetirEmail && currentEmail !== '') {
                delete newErrors.email;
                delete newErrors.repetirEmail;
                errorUpdated = true;
            } else if (currentEmail !== currentRepetirEmail && currentRepetirEmail !== '') {
                newErrors.repetirEmail = 'Los emails no coinciden';
                errorUpdated = true;
            }
        }
        return errorUpdated;
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'fechaDeNacimiento') return; 
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        const newErrors = { ...errors };
        if (value.trim() !== '') {
            delete newErrors[name];
        } else {
            const requiredFields = [
                'nombre', 'apellido', 'fechaDeNacimiento', 'contrasena', 'repetirContrasena',
                'email', 'repetirEmail', 'telefono', 'calle', 'altura', 'ciudad',
                'codigoPostal', 'pais'
            ];
            if (requiredFields.includes(name)) {
                newErrors[name] = 'Este campo es obligatorio';
            }
        }
        checkMatchError(name, value, newErrors);
        setErrors(newErrors);
    };
const handleDateChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = formatDateInput(value); // Aplica la máscara DD/MM/YYYY
    
    setFormData(prevData => ({
        ...prevData,
        [name]: formattedValue
    }));

    const newErrors = { ...errors };

    if (formattedValue.trim() === '') {
        // Campo obligatorio
        newErrors[name] = 'Este campo es obligatorio';
    } else if (formattedValue.length === 10) {
        // La entrada tiene 10 caracteres (DD/MM/YYYY), ahora verificamos formato y edad.
        
        // La función isAdult internamente ya retorna false si el formato no es DD/MM/YYYY
        // o si es una fecha no real (ej: 30/02/2000), o si es menor de 18.
        if (!isAdult(formattedValue)) {
            // Este mensaje ahora cubre tanto ser menor de edad como una fecha inválida
            // (que isAdult interpreta como no mayor de edad porque retorna false).
            newErrors.fechaDeNacimiento = 'Debes ser mayor de edad (18 años) para inscribirte';
        } else {
            // Fecha válida y mayor de edad
            delete newErrors.fechaDeNacimiento;
        }
    } else {
        // La fecha está incompleta (longitud < 10) pero no vacía. 
        // Eliminamos el error de edad para que no moleste mientras escribe, 
        // pero se validará en handleSubmit si no termina de escribir.
        delete newErrors.fechaDeNacimiento; 
    }

    setErrors(newErrors);
};

// Función de validación final antes del envío - AJUSTADA
const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
        'nombre', 'apellido', 'fechaDeNacimiento',
        'contrasena', 'repetirContrasena',
        'email', 'repetirEmail', 'telefono', 
        'calle', 'altura', 'ciudad',
        'codigoPostal', 'pais'
    ];
    requiredFields.forEach(fieldId => {
        if (!formData[fieldId] || formData[fieldId].trim() === '') {
            newErrors[fieldId] = 'Este campo es obligatorio';
        }
    });

    // ÚNICA validación de fecha: Si el campo NO está vacío, comprobamos si cumple con el requisito de edad/validez.
    // Si la fecha es '01/01/20', isAdult fallará. Si es '99/99/9999', isAdult fallará.
    // Si es '01/01/2020' (menor de edad), isAdult fallará.
    if (formData.fechaDeNacimiento && !newErrors.fechaDeNacimiento && !isAdult(formData.fechaDeNacimiento)) {
        newErrors.fechaDeNacimiento = 'Debes ser mayor de edad (18 años) para inscribirte';
    }
    
    // ... (Validaciones de contraseñas y emails) ...
    if (formData.contrasena !== formData.repetirContrasena) {
        newErrors.repetirContrasena = 'Las contraseñas no coinciden';
    }
    if (formData.email !== formData.repetirEmail) {
        newErrors.repetirEmail = 'Los emails no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            const dataForSubmission = { ...formData };
            dataForSubmission.fechaDeNacimiento = formatDateForSubmission(formData.fechaDeNacimiento);
            try {
                const response = await fetch('https://backend-288175208699.europe-west1.run.app/register_waitlist', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(dataForSubmission),
                });
                const result = await response.json();
                if (response.ok) {
                    if (result.estado_lista === 'Activo') {
                        setIsActivoModalOpen(true);
                    } else {
                        setIsModalOpen(true);
                    }
                    setFormData(initialFormData);
                    setErrors({});
                } else {
                    showToast(`Error: ${result.error}`, 'error');
                    console.error("Error del servidor:", result.error);
                }
            } catch (error) {
                showToast("Error de conexión con el servidor.", 'error');
                console.error("Error de red o fetch:", error);
            }
        } else {
            console.log('Formulario inválido, revisa los campos.');
        }
    };
    const getInputClassName = (fieldId) => {
        return errors[fieldId] ? 'input-error-borde' : '';
    };

    // El form completo de acá abajo es solo para quien no tiene cuenta todavía.
    if (authLoading) {
        return <LoadingSpinner />;
    }
    if (user) {
        const estadoLista = user.profileData?.estadoLista;
        if (estadoLista === 'Registrado') {
            return <SumarseALaListaRegistrado />;
        }
        // Ya está en la lista (Espera/Activo) o es staff: no hay nada que hacer acá.
        return <Navigate to="/perfil" replace />;
    }

return (
<div className="historia-container">
<SuccessModal
isOpen={isModalOpen}
onClose={() => setIsModalOpen(false)}
message="¡Te has inscrito exitosamente a nuestra lista de espera exclusiva! Recibirás una notificación por email cuando tu cupo esté disponible."
/>
<ActivoPopup
isOpen={isActivoModalOpen}
onClose={() => setIsActivoModalOpen(false)}
/>
<div className="contenido-scrollable">
<div className="titulo-principal">
<hr className="linea-horizontal" />
<h1>ACCESO EXCLUSIVO: PROYECTO P195</h1>
<hr className="linea-horizontal" />
</div>
<p className="contenido-centrado">
La producción de P195 es extremadamente limitada para garantizar la máxima calidad en cada botella. Por este motivo, la única forma de adquirir nuestros vinos es inscribiéndose en la lista de espera.
<div style={{height:'10px'}}></div>La inscripción a esta lista no garantiza un cupo automático. Las asignaciones se realizan por estricto orden de registro, a medida que surgen disponibilidades por la baja de clientes activos.
{FEATURES.beneficiosListaEspera && <><div style={{height:'10px'}}></div>Mientras aguarda su cupo, usted tendrá acceso a los siguientes beneficios exclusivos:</>}
</p>
{FEATURES.beneficiosListaEspera && (
<ul className="reglas-lista">
<li>Acceso prioritario a los eventos realizados en la finca, con hasta 4 invitados.</li>
<li>Degustaciones sin cargo.</li>
<li>Acceso a las avant premières de nuestras nuevas creaciones.</li>
<li>Demostraciones de maridajes y cocina gourmet.</li>
</ul>
)}
<p className="contenido-centrado" style={{paddingBottom:'15px', color: '#8f1941'}}>
(*) Campos obligatorios
</p>
<p className="contenido-centrado" style={{paddingBottom:'15px'}}>
Para registrarse, por favor complete el siguiente formulario:
</p>
<form className="formulario-lista" onSubmit={handleSubmit} noValidate>
<h2>información de contacto</h2>
<div className="form-group-grid">
<div className="form-group">
<label htmlFor="nombre"><p className='asterisco'>*</p>Nombre</label>
<div className="input-con-error">
<input type="text" id="nombre" name="nombre" value={formData.nombre} onChange={handleChange} className={getInputClassName('nombre')} />
{errors.nombre && <MensajeError mensaje={errors.nombre} />}
</div>
</div>
<div className="form-group">
<label htmlFor="apellido"><p className='asterisco'>*</p>Apellido</label>
<div className="input-con-error">
<input type="text" id="apellido" name="apellido" value={formData.apellido} onChange={handleChange} className={getInputClassName('apellido')} />
{errors.apellido && <MensajeError mensaje={errors.apellido} />}
</div>
</div>
<div className="form-group">
<label htmlFor="fechaDeNacimiento"><p className='asterisco'>*</p>Fecha de Nacimiento</label>
<div className="input-con-error">
<input 
type="text"
maxLength="10"
id="fechaDeNacimiento" 
name="fechaDeNacimiento" 
value={formData.fechaDeNacimiento} 
onChange={handleDateChange}
className={getInputClassName('fechaDeNacimiento')}
placeholder="DD/MM/YYYY"
style={{textAlign:'center'}}
/>
{errors.fechaDeNacimiento && <MensajeError mensaje={errors.fechaDeNacimiento} />}
</div>
</div>
</div> 
<h2>creación de usuario</h2>
<div className="form-group-grid">
<div className="form-group">
<label htmlFor="contrasena"><p className='asterisco'>*</p>Contraseña</label>
<div className="input-con-error">
<div className="input-password-wrapper">
<input
type={showPassword ? 'text' : 'password'} 
id="contrasena"
name="contrasena"
value={formData.contrasena}
onChange={handleChange}
className={getInputClassName('contrasena')}
/>
<button
type="button"
className="password-toggle-button"
onClick={() => togglePasswordVisibility('contrasena')}
>
{showPassword ? <NoSeeIcon style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}} /> : <SeeIcon style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}} />} 
</button>
</div>
{errors.contrasena && <MensajeError mensaje={errors.contrasena} />}
</div>
</div>
<div className="form-group">
<label htmlFor="repetirContrasena"><p className='asterisco'>*</p>Repetir Contraseña</label>
<div className="input-con-error">
<div className="input-password-wrapper">
<input
type={showRepeatPassword ? 'text' : 'password'}
id="repetirContrasena"
name="repetirContrasena"
value={formData.repetirContrasena}
onChange={handleChange}
className={getInputClassName('repetirContrasena')}
/>
<button
type="button"
className="password-toggle-button"
onClick={() => togglePasswordVisibility('repetirContrasena')}
>
{showRepeatPassword ? <NoSeeIcon style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}}/> : <SeeIcon style={{height:'15px',overflow:'visible', width:'auto',margin:'auto 0'}}/>}
</button>
</div>
{errors.repetirContrasena && <MensajeError mensaje={errors.repetirContrasena} />}
</div>
</div>
</div>
<div className="form-group-grid">
<div className="form-group">
<label htmlFor="titulo">Título</label>
<input type="text" id="titulo" name="titulo" value={formData.titulo} onChange={handleChange} />
</div>
<div className="form-group">
<label htmlFor="empresa">Empresa</label>
<input type="text" id="empresa" name="empresa" value={formData.empresa} onChange={handleChange} />
</div>
</div>
<div className="form-group-grid">
<div className="form-group">
<label htmlFor="email"><p className='asterisco'>*</p>Email</label>
<div className="input-con-error">
<div className="input-password-wrapper">
<input 
type={showEmail ? 'text' : 'email'} 
id="email" 
name="email" 
value={formData.email} 
onChange={handleChange} 
className={getInputClassName('email')} 
/>
<button
type="button"
className="password-toggle-button"
onClick={() => togglePasswordVisibility('email')}
>
</button>
</div>
{errors.email && <MensajeError mensaje={errors.email} />}
</div>
</div>
<div className="form-group">
<label htmlFor="repetirEmail"><p className='asterisco'>*</p>Repetir Email</label>
<div className="input-con-error">
<div className="input-password-wrapper">
<input 
type={showRepeatEmail ? 'text' : 'email'} 
id="repetirEmail" 
name="repetirEmail" 
value={formData.repetirEmail} 
onChange={handleChange} 
className={getInputClassName('repetirEmail')} 
/>
<button
type="button"
className="password-toggle-button"
onClick={() => togglePasswordVisibility('repetirEmail')}
>
</button>
</div>
{errors.repetirEmail && <MensajeError mensaje={errors.repetirEmail} />}
</div>
</div>
<div className="form-group">
<label htmlFor="telefono"><p className='asterisco'>*</p>Teléfono</label>
<div className="input-con-error">
<input type="tel" id="telefono" name="telefono" value={formData.telefono} onChange={handleChange} className={getInputClassName('telefono')} />
{errors.telefono && <MensajeError mensaje={errors.telefono} />}
</div>
</div>
</div>
<div className="form-group-grid direccion-grid">
<div className="form-group">
<label htmlFor="calle"><p className='asterisco'>*</p>Calle</label>
<div className="input-con-error">
<input 
type="text" 
id="calle" 
name="calle" 
value={formData.calle} 
onChange={handleChange} 
className={getInputClassName('calle')}
/>
{errors.calle && <MensajeError mensaje={errors.calle} />}
</div>
</div>
<div className="form-group">
<label htmlFor="altura"><p className='asterisco'>*</p>Altura</label>
<div className="input-con-error">
<input 
type="text" 
id="altura" 
name="altura" 
value={formData.altura} 
onChange={handleChange} 
className={getInputClassName('altura')}
/>
{errors.altura && <MensajeError mensaje={errors.altura} />}
</div>
</div>
<div className="form-group">
<label htmlFor="ciudad"><p className='asterisco'>*</p>Ciudad/ Localidad/ Provincia</label>
<div className="input-con-error">
<input 
type="text" 
id="ciudad" 
name="ciudad" 
value={formData.ciudad} 
onChange={handleChange} 
className={getInputClassName('ciudad')}
/>
{errors.ciudad && <MensajeError mensaje={errors.ciudad} />}
</div>
</div>
<div className="form-group">
<label htmlFor="codigoPostal"><p className='asterisco'>*</p>Código Postal</label>
<div className="input-con-error">
<input type="text" id="codigoPostal" name="codigoPostal" value={formData.codigoPostal} onChange={handleChange} className={getInputClassName('codigoPostal')} />
{errors.codigoPostal && <MensajeError mensaje={errors.codigoPostal} />}
</div>
</div>
<div className="form-group">
<label htmlFor="pais"><p className='asterisco'>*</p>País</label>
<div className="input-con-error">
<input type="text" id="pais" name="pais" value={formData.pais} onChange={handleChange} className={getInputClassName('pais')} />
{errors.pais && <MensajeError mensaje={errors.pais} />}
</div>
</div>
</div>
<hr className="linea-horizontal" style={{margin:'30px 0 15px', padding:'0'}}/>
<button type="submit" className="boton-inscribirse">
Inscribirse
</button>
</form>
</div>
</div>
);
}
export default ListaDeEspera;